import type { ViewFieldsFragment, ViewForSiderFieldsFragment, ViewGroupFieldsFragment } from '@salescore/client-api'
import { searchParentViewGroups } from '@salescore/client-common'
import { Array, Record } from 'effect'
import { useCallback, useMemo } from 'react'
import { useRecoilValue } from 'recoil'

import { viewsContextAtom } from '../atoms'

export const useSearchParentViewGroups = (): ((
  viewGroupId: string | undefined | null,
) => ViewGroupFieldsFragment[]) => {
  const { viewGroups } = useRecoilValue(viewsContextAtom)
  const viewGroupsMap = useMemo(() => Record.fromIterableBy(Array.reverse(viewGroups), (x) => x.id), [viewGroups])

  return useCallback(
    (viewGroupId: string | undefined | null) => searchParentViewGroups(viewGroupId, viewGroupsMap),
    [viewGroupsMap],
  )
}

export const useSearchViewGroup = (): ((
  viewGroupId: string | undefined | null,
) => undefined | ViewGroupFieldsFragment) => {
  const { viewGroups } = useRecoilValue(viewsContextAtom)
  const viewGroupsMap = useMemo(() => Record.fromIterableBy(Array.reverse(viewGroups), (x) => x.id), [viewGroups])
  return useCallback(
    (viewGroupId: string | undefined | null) => {
      if (viewGroupId === undefined || viewGroupId === null) {
        return
      }
      return viewGroupsMap[viewGroupId]
    },
    [viewGroupsMap],
  )
}

export const useSearchView = (): ((viewId: string | undefined) => undefined | ViewFieldsFragment) => {
  const { views } = useRecoilValue(viewsContextAtom)
  const viewsMap = useMemo(() => Record.fromIterableBy(Array.reverse(views), (x) => x.id), [views])

  return useCallback(
    (viewId: string | undefined) => {
      if (viewId === undefined) {
        return
      }
      return viewsMap[viewId]
    },
    [viewsMap],
  )
}

export const useKpiViews = (): ViewForSiderFieldsFragment[] => {
  const { viewsForSider } = useRecoilValue(viewsContextAtom)

  return useMemo(() => viewsForSider.filter((view) => view.type === 'kpi'), [viewsForSider])
}

export const useViewGroupsWithParents = (): Array<{
  viewGroup: ViewGroupFieldsFragment
  parents: ViewGroupFieldsFragment[]
}> => {
  const { viewGroups } = useRecoilValue(viewsContextAtom)
  return useMemo(() => {
    const viewGroupsMap = Record.fromIterableBy(Array.reverse(viewGroups), (x) => x.id)
    return viewGroups.map((viewGroup) => ({
      viewGroup,
      parents: searchParentViewGroups(viewGroup.viewGroupId, viewGroupsMap),
    }))
  }, [viewGroups])
}
